import { Component, ViewChild, Input, HostBinding, ViewEncapsulation, forwardRef, inject, output, OnDestroy, DestroyRef } from "@angular/core";
import { ComboBoxComponent, DropDownSize } from "@progress/kendo-angular-dropdowns";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TooltipDirective } from "@progress/kendo-angular-tooltip";
import { ITranslateObject } from "../../../views-interfaces";
import { WkTranslatePipe, NestedValuePipe } from "src/app/shared";

declare module "@progress/kendo-angular-dropdowns" {
    interface ComboBoxComponent {
        name: string;
    }
}

@Component({
    selector: "kendo-combobox-wrapper",
    templateUrl: "./kendo-combobox-wrapper.component.html",
    styleUrl: "./kendo-combobox-wrapper.component.scss",
    standalone: false,
    host: {
        class: "wk-cbw",
        "cg3-theme": "PDS-v3",
    },
    encapsulation: ViewEncapsulation.None,
    providers: [
        WkTranslatePipe,
        NestedValuePipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KendoComboBoxWrapper),
            multi: true,
        },
    ],
})
class KendoComboBoxWrapper implements ControlValueAccessor {
    private wkTranslate = inject(WkTranslatePipe);
    private nestedValue = inject(NestedValuePipe);
    private hostKeydownListener: ((event: KeyboardEvent) => void) | null = null;
    private searchbarClickListener: ((event: MouseEvent) => void) | null = null;

    constructor() {
        const destroyRef = inject(DestroyRef);
        destroyRef.onDestroy(() => {
            if (this.hostKeydownListener && this._combobox?.hostElement?.nativeElement) {
                this._combobox.hostElement.nativeElement.removeEventListener("keydown", this.hostKeydownListener, true);
            }
            if (this.searchbarClickListener && this._combobox?.searchbar?.input?.nativeElement) {
                this._combobox.searchbar.input.nativeElement.removeEventListener("click", this.searchbarClickListener, true);
            }
        });
    }

    @Input() name: string;
    @Input() textField = "label";
    @Input() valueField = "id";
    @Input() size: DropDownSize = "medium";
    @Input() disabled = false;
    @Input() placeholder = "";
    @Input() noDataText = "";
    @Input() virtual = false;
    readonly filterChange = output<string>();
    @Input() label?: string | ITranslateObject = "";

    filteredData: any[] = [];
    private _data: any = null;
    @Input()
    set data(val: any) {
        this._data = val;
        this.filteredData = val;
    }
    get data() {
        return this._data;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    handleValueChange(value: any): void {
        this._value = value;
        if (this._combobox) {
            this._combobox.value = value === "" ? null : value;
        }
        this.onChange(value);
        this.onTouched();
    }

    private onChange: (value: any) => void = () => {};
    public onTouched: () => void = () => {};

    private _combobox!: ComboBoxComponent;
    @ViewChild(ComboBoxComponent, { static: false })
    set combobox(component: ComboBoxComponent) {
        if (component && this._combobox == null) {
            this._combobox = component;
            this.initializeComboBoxComponent(this._combobox);
        }
    }
    get combobox(): ComboBoxComponent {
        return this._combobox;
    }

    private _value: any = null;
    @Input()
    set value(val: any) {
        this.writeValue(val);
    }
    get value() {
        return this._value;
    }

    writeValue(value: any): void {
        this._value = value;
        if (this._combobox) {
            this._combobox.writeValue(value);
        }
    }

    initializeComboBoxComponent(component: ComboBoxComponent): void {
        component.name = this.name;

        if (component.valueChange) {
            component.valueChange.subscribe((value: any) => {
                this.handleValueChange(value);
            });
        }

        this.hostKeydownListener = (event: KeyboardEvent) => {
            if (event.key === "Backspace" || event.keyCode === 8) {
                const target = event.target as HTMLElement;
                if (target.classList.contains("k-input-inner")) {
                    const input = target as HTMLInputElement;
                    if (input.value !== "") return;
                }
                event.preventDefault();
                event.stopPropagation();
            }
        };
        component.hostElement.nativeElement.addEventListener("keydown", this.hostKeydownListener, true);

        this.searchbarClickListener = (event: MouseEvent) => {
            if (component.isOpen) return;
            component.toggle(true);
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        };
        component.searchbar.input.nativeElement.addEventListener("click", this.searchbarClickListener, true);
    }

    onFilterChange(filter: string): void {
        if (filter === undefined || filter === null || filter === "") {
            this.filteredData = this.data;
            this.filterChange.emit(filter);
            return;
        }

        this.filteredData = this.data.filter((item) => {
            const text = this.wkTranslate.transform(this.nestedValue.transform(item, this.textField));
            return this.normalizeText(text).includes(this.normalizeText(filter));
        });

        this.filterChange.emit(filter);
    }

    private normalizeText(text: string): string {
        return text
            .toLowerCase()
            .normalize("NFD")
            .replaceAll(/[\u0300-\u036f]/g, "");
    }

    @HostBinding("class.wk-cbw--open")
    get isDropdownOpen() {
        return this._combobox ? this._combobox.isOpen : false;
    }

    @HostBinding("attr.size")
    get hostSize() {
        return this.size;
    }

    @ViewChild(TooltipDirective) tooltipDir!: TooltipDirective;
    private showTimeout: any;
    private hideTimeout: any;

    onMouseOver(e: MouseEvent): void {
        const target = e.target as HTMLElement | null;
        const el = target?.closest("li.k-list-item, .k-input-inner") as HTMLElement | null;

        if (!el) {
            this.tooltipDir?.hide();
            return;
        }

        const textEl = el.querySelector<HTMLElement>(".combobox-wrapper-item, input") ?? el;
        const isOverflowing = textEl.scrollWidth > textEl.clientWidth || textEl.scrollHeight > textEl.clientHeight;

        if (isOverflowing) {
            clearTimeout(this.hideTimeout);
            clearTimeout(this.showTimeout);
            this.showTimeout = setTimeout(() => {
                this.tooltipDir.toggle(el, true);
            }, 600);
        } else {
            this.tooltipDir.hide();
        }
    }

    onMouseLeave(event: MouseEvent) {
        clearTimeout(this.showTimeout);

        const tooltipEl = document.querySelector(".kendo-combobox-wrapper-tooltip.k-animation-container") as HTMLElement;
        if (tooltipEl) {
            tooltipEl.classList.add("cg3-tooltip-closing");
        }

        this.hideTimeout = setTimeout(() => {
            this.tooltipDir.hide();
        }, 300);
    }
}

export { KendoComboBoxWrapper };
